import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react";

const wrapPrimitiveValuesInTokenObject = <T extends Record<string, any>>(obj: T): any => {
    const result: Record<string, any> = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
                // If it's a nested object (like color shades), recurse
                result[key] = wrapPrimitiveValuesInTokenObject(value);
            } else if (typeof value === 'string' || typeof value === 'number') {
                // Wrap primitive string or number values in { value: T }
                result[key] = { value: value };
            } else {
                // Keep other types (e.g., booleans, null, undefined) as is
                result[key] = value;
            }
        }
    }
    return result;
};


export const createTheme = (colorPalette: string, customColors?: Record<string, string>) => {
    // Extracted NYSDS Primitive Tokens
    const nysdsPrimitiveTokens = {
        colors: {
            neutral: {
                '10': '#f6f6f6', '50': '#ededed', '100': '#d0d0ce', '200': '#bec0c1', '300': '#a7a9ab',
                '400': '#909395', '500': '#797c7f', '600': '#62666a', '700': '#4a4d4f', '800': '#323435', '900': '#1b1b1b',
            },
            black: '#000000',
            white: '#ffffff',
            red: {
                '10': '#fbf4f4', '50': '#f7eaea', '100': '#f0d5d5', '200': '#e4b3b3', '300': '#d89191',
                '400': '#cc6f6f', '500': '#c04d4d', '600': '#b52c2c', '700': '#932424', '800': '#721c1c', '900': '#511414',
            },
            blue: {
                '10': '#f2f7fc', '50': '#e5effa', '100': '#cce0f6', '200': '#a3c2ee', '300': '#7aa5e7',
                '400': '#5187df', '500': '#286ad8', '600': '#004dd1', '700': '#003ba1', '800': '#002971', '900': '#001842',
            },
            green: {
                '10': '#f3f8f4', '50': '#e8f1ea', '100': '#d2e3d5', '200': '#aecdb3', '300': '#8ab792',
                '400': '#66a170', '500': '#428b4f', '600': '#1e752e', '700': '#165923', '800': '#0f3d18', '900': '#08210d',
            },
            yellow: {
                '10': '#fefcf2', '50': '#fefae5', '100': '#fef5cc', '200': '#fce888', '300': '#fbdb44',
                '400': '#face00', '500': '#d6b000', '600': '#b29200', '700': '#8e7500', '800': '#6a5700', '900': '#463a00',
            },
            stateBlue: {
                '10': '#f7fafd', '50': '#eff6fb', '100': '#cddde9', '200': '#abc4d8', '300': '#89abc7',
                '400': '#6792b6', '500': '#457aa5', '600': '#2d618c', '700': '#154973', '800': '#0e324f', '900': '#081b2b',
            },
            businessTeal: {
                '10': '#f2f6f6', '50': '#e5eded', '100': '#c5d8d9', '200': '#a5c3c5', '300': '#85aeb1',
                '400': '#65999e', '500': '#387b82', '600': '#0b5d66', '700': '#084b52', '800': '#05393f', '900': '#02272c',
            },
            healthPurple: {
                '10': '#f4f4f8', '50': '#e9eaf1', '100': '#d3d5e3', '200': '#bbb8cc', '300': '#a39bb6',
                '400': '#8b7fa0', '500': '#736289', '600': '#5b4573', '700': '#43285d', '800': '#2c1a3e', '900': '#160d1f',
            },
            localBrown: {
                '10': '#f6f5f4', '50': '#eeebe9', '100': '#ddd7d3', '200': '#c3b9b3', '300': '#a99b94',
                '400': '#8f7d75', '500': '#745e55', '600': '#5a4036', '700': '#402217', '800': '#2a160f', '900': '#150b07',
            },
            environmentGreen: {
                '10': '#f5f7f6', '50': '#ecf0ee', '100': '#d9e1dd', '200': '#bac6bf', '300': '#9caba1',
                '400': '#7e9084', '500': '#5f7566', '600': '#415a48', '700': '#233f2b', '800': '#172a1c', '900': '#0b150e',
            },
            adminOrange: {
                '10': '#fcf4ef', '50': '#f9e9e0', '100': '#f3d3c1', '200': '#e7b59c', '300': '#db9778',
                '400': '#d07954', '500': '#c0623b', '600': '#b14b22', '700': '#a2350a', '800': '#752607', '900': '#481704',
            },
            transportationBlue: {
                '10': '#f4f8fa', '50': '#eaf2f5', '100': '#d5e5eb', '200': '#b2ccd9', '300': '#90b3c7',
                '400': '#6e9ab5', '500': '#4d83a4', '600': '#2c6c93', '700': '#0b5583', '800': '#093a5c', '900': '#072036',
            },
            safetyGray: {
                '10': '#f2f5f6', '50': '#e5ebed', '100': '#cdd6da', '200': '#b5c1c8', '300': '#9dacb5',
                '400': '#8697a3', '500': '#6f8391', '600': '#59707f', '700': '#435d6e', '800': '#30424f', '900': '#1d2830',
            },
            transparent: '#ffffff00',
            blackTransparent: {
                '50': '#00000003', '100': '#0000001a', '200': '#00000033', '300': '#0000004d', '400': '#00000066',
                '500': '#00000080', '600': '#00000099', '700': '#000000b3', '800': '#000000cc', '900': '#000000e6',
            },
            whiteTransparent: {
                '50': '#ffffff03', '100': '#ffffff1a', '200': '#ffffff33', '300': '#ffffff4d', '400': '#ffffff66',
                '500': '#ffffff80', '600': '#ffffff99', '700': '#ffffffb3', '800': '#ffffffcc', '900': '#ffffffe6',
            },
        },
        fonts: {
            sans: 'Proxima Nova, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol',
            serif: 'Noto Serif, Georgia, Cambria, Times New Roman, Times, serif',
            alt: 'Oswald, Arial, sans-serif',
            monospace: 'Roboto Mono Web, Bitstream Vera Sans Mono, Consolas, Courier, monospace',
            agency: 'D Sari, Arial, sans-serif',
        },
        fontSizes: {
            '2xs': '.625rem', xs: '.75rem', sm: '.875rem', md: '1rem', lg: '1.125rem', xl: '1.25rem',
            '2xl': '1.375rem', '3xl': '1.5rem', '4xl': '1.75rem', '5xl': '2rem', '6xl': '2.25rem',
            '7xl': '2.5rem', '8xl': '2.75rem', '9xl': '3.125rem',
        },
        fontWeights: {
            thin: '100', extralight: '200', light: '300', regular: '400', medium: '500',
            semibold: '600', bold: '700', extrabold: '800', black: '900',
        },
        lineHeights: {
            bodyXs: '20px', bodySm: '24px', bodyMd: '24px',
            uiXs: '20px', uiSm: '24px', uiMd: '24px', uiLg: '28px', uiXl: '28px',
            displaySm: '44px', displayMd: '48px', displayLg: '52px', displayXl: '60px',
            h1: '40px', h2: '36px', h3: '32px', h4: '32px', h5: '28px', h6: '28px',
            raw2xs: '18px', rawXs: '21px', rawSm: '22px', rawMd: '25px', rawLg: '28px', rawXl: '31px',
            raw2xl: '39px', raw3xl: '45px', raw4xl: '57px',
        },
        letterSpacings: {
            negative200: '-.02rem', negative100: '-.01rem', '0': '0rem', '100': '.001rem',
            '200': '.001rem', '300': '.002rem', '400': '.003rem', '500': '.003rem', '600': '.004rem',
        },
        space: {
            '1px': '1px', '2px': '2px', '50': '4px', '100': '8px', '150': '12px', '200': '16px',
            '250': '20px', '300': '24px', '400': '32px', '500': '40px', '600': '48px', '700': '56px',
            '800': '64px', '1200': '96px',
        },
        sizes: {
            '1px': '1px', '2px': '2px', '50': '4px', '100': '8px', '150': '12px', '200': '16px',
            '250': '20px', '300': '24px', '400': '32px', '450': '36px', '500': '40px', '550': '44px',
            '600': '48px', '700': '56px', '800': '64px', '1200': '96px',
            formSm: '88px', formMd: '200px', formLg: '384px',
            iconXs: '.75rem', iconSm: '.875rem', iconMd: '1rem', iconLg: '1.125rem', iconXl: '1.25rem',
            icon2xl: '1.375rem', icon3xl: '1.5rem', icon4xl: '1.75rem', icon5xl: '2rem',
        },
        borderWidths: {
            sm: '1px', md: '2px', lg: '4px',
        },
        radii: {
            xs: '1px', sm: '2px', md: '4px', lg: '8px', xl: '12px', xxl: '16px', round: '1776px',
        },
        gutters: {
            xs: '20px', sm: '20px', md: '32px', lg: '32px', xl: '64px',
        }
    };

  const wrappedNysdsTokens = wrapPrimitiveValuesInTokenObject(nysdsPrimitiveTokens);

    const config = defineConfig({
        globalCss: {
            html: {
                colorPalette: colorPalette,
            },
        },
        theme: {
            tokens: {
                colors: {
                    nysds: {
                        neutral: {
                            10: { value: '#f6f6f6' },
                            50: { value: '#ededed' },
                            100: { value: '#d0d0ce' },
                            200: { value: '#bec0c1' },
                            300: { value: '#a7a9ab' },
                            400: { value: '#909395' },
                            500: { value: '#797c7f' },
                            600: { value: '#62666a' },
                            700: { value: '#4a4d4f' },
                            800: { value: '#323435' },
                            900: { value: '#1b1b1b' },
                        },
                        black: { value: '#000000' },
                        white: { value: '#ffffff' },
                        red: {
                            10: { value: '#fbf4f4' },
                            50: { value: '#f7eaea' },
                            100: { value: '#f0d5d5' },
                            200: { value: '#e4b3b3' },
                            300: { value: '#d89191' },
                            400: { value: '#cc6f6f' },
                            500: { value: '#c04d4d' },
                            600: { value: '#b52c2c' },
                            700: { value: '#932424' },
                            800: { value: '#721c1c' },
                            900: { value: '#511414' },
                        },
                        blue: {
                            10: { value: '#f2f7fc' },
                            50: { value: '#e5effa' },
                            100: { value: '#cce0f6' },
                            200: { value: '#a3c2ee' },
                            300: { value: '#7aa5e7' },
                            400: { value: '#5187df' },
                            500: { value: '#286ad8' },
                            600: { value: '#004dd1' },
                            700: { value: '#003ba1' },
                            800: { value: '#002971' },
                            900: { value: '#001842' },
                        },
                        green: {
                            10: { value: '#f3f8f4' },
                            50: { value: '#e8f1ea' },
                            100: { value: '#d2e3d5' },
                            200: { value: '#aecdb3' },
                            300: { value: '#8ab792' },
                            400: { value: '#66a170' },
                            500: { value: '#428b4f' },
                            600: { value: '#1e752e' },
                            700: { value: '#165923' },
                            800: { value: '#0f3d18' },
                            900: { value: '#08210d' },
                        },
                        yellow: {
                            10: { value: '#fefcf2' },
                            50: { value: '#fefae5' },
                            100: { value: '#fef5cc' },
                            200: { value: '#fce888' },
                            300: { value: '#fbdb44' },
                            400: { value: '#face00' },
                            500: { value: '#d6b000' },
                            600: { value: '#b29200' },
                            700: { value: '#8e7500' },
                            800: { value: '#6a5700' },
                            900: { value: '#463a00' },
                        },
                        stateBlue: {
                            10: { value: '#f7fafd' },
                            50: { value: '#eff6fb' },
                            100: { value: '#cddde9' },
                            200: { value: '#abc4d8' },
                            300: { value: '#89abc7' },
                            400: { value: '#6792b6' },
                            500: { value: '#457aa5' },
                            600: { value: '#2d618c' },
                            700: { value: '#154973' },
                            800: { value: '#0e324f' },
                            900: { value: '#081b2b' },
                        },
                        businessTeal: {
                            10: { value: '#f2f6f6' },
                            50: { value: '#e5eded' },
                            100: { value: '#c5d8d9' },
                            200: { value: '#a5c3c5' },
                            300: { value: '#85aeb1' },
                            400: { value: '#65999e' },
                            500: { value: '#387b82' },
                            600: { value: '#0b5d66' },
                            700: { value: '#084b52' },
                            800: { value: '#05393f' },
                            900: { value: '#02272c' },
                        },
                        healthPurple: {
                            10: { value: '#f4f4f8' },
                            50: { value: '#e9eaf1' },
                            100: { value: '#d3d5e3' },
                            200: { value: '#bbb8cc' },
                            300: { value: '#a39bb6' },
                            400: { value: '#8b7fa0' },
                            500: { value: '#736289' },
                            600: { value: '#5b4573' },
                            700: { value: '#43285d' },
                            800: { value: '#2c1a3e' },
                            900: { value: '#160d1f' },
                        },
                        localBrown: {
                            10: { value: '#f6f5f4' },
                            50: { value: '#eeebe9' },
                            100: { value: '#ddd7d3' },
                            200: { value: '#c3b9b3' },
                            300: { value: '#a99b94' },
                            400: { value: '#8f7d75' },
                            500: { value: '#745e55' },
                            600: { value: '#5a4036' },
                            700: { value: '#402217' },
                            800: { value: '#2a160f' },
                            900: { value: '#150b07' },
                        },
                        environmentGreen: {
                            10: { value: '#f5f7f6' },
                            50: { value: '#ecf0ee' },
                            100: { value: '#d9e1dd' },
                            200: { value: '#bac6bf' },
                            300: { value: '#9caba1' },
                            400: { value: '#7e9084' },
                            500: { value: '#5f7566' },
                            600: { value: '#415a48' },
                            700: { value: '#233f2b' },
                            800: { value: '#172a1c' },
                            900: { value: '#0b150e' },
                        },
                        adminOrange: {
                            10: { value: '#fcf4ef' },
                            50: { value: '#f9e9e0' },
                            100: { value: '#f3d3c1' },
                            200: { value: '#e7b59c' },
                            300: { value: '#db9778' },
                            400: { value: '#d07954' },
                            500: { value: '#c0623b' },
                            600: { value: '#b14b22' },
                            700: { value: '#a2350a' },
                            800: { value: '#752607' },
                            900: { value: '#481704' },
                        },
                        transportationBlue: {
                            10: { value: '#f4f8fa' },
                            50: { value: '#eaf2f5' },
                            100: { value: '#d5e5eb' },
                            200: { value: '#b2ccd9' },
                            300: { value: '#90b3c7' },
                            400: { value: '#6e9ab5' },
                            500: { value: '#4d83a4' },
                            600: { value: '#2c6c93' },
                            700: { value: '#0b5583' },
                            800: { value: '#093a5c' },
                            900: { value: '#072036' },
                        },
                        safetyGray: {
                            10: { value: '#f2f5f6' },
                            50: { value: '#e5ebed' },
                            100: { value: '#cdd6da' },
                            200: { value: '#b5c1c8' },
                            300: { value: '#9dacb5' },
                            400: { value: '#8697a3' },
                            500: { value: '#6f8391' },
                            600: { value: '#59707f' },
                            700: { value: '#435d6e' },
                            800: { value: '#30424f' },
                            900: { value: '#1d2830' },
                        },
                        transparent: { value: '#ffffff00' },
                        blackTransparent: {
                            50: { value: '#00000003' },
                            100: { value: '#0000001a' },
                            200: { value: '#00000033' },
                            300: { value: '#0000004d' },
                            400: { value: '#00000066' },
                            500: { value: '#00000080' },
                            600: { value: '#00000099' },
                            700: { value: '#000000b3' },
                            800: { value: '#000000cc' },
                            900: { value: '#000000e6' },
                        },
                        whitTransparent: {
                            50: { value: '#ffffff03' },
                            100: { value: '#ffffff1a' },
                            200: { value: '#ffffff33' },
                            300: { value: '#ffffff4d' },
                            400: { value: '#ffffff66' },
                            500: { value: '#ffffff80' },
                            600: { value: '#ffffff99' },
                            700: { value: '#ffffffb3' },
                            800: { value: '#ffffffcc' },
                            900: { value: '#ffffffe6' },
                        },
                    },
                    otda: {
                        50: { value: "#ffffff" },
                        100: { value: "#c3e1f7" },
                        200: { value: "#e3f2ff" },
                        300: { value: "#7392ab" },
                        400: { value: "#446d8f" },
                        500: { value: "#154973" },
                        600: { value: "#11334f" },
                        700: { value: "#185e96" },
                        800: { value: "#062036" },
                        900: { value: "#040f17" },
                        950: { value: "#000000" },
                    },
                    custom: {
                        50: { value: customColors?.['50'] || "#ffffff" },
                        100: { value: customColors?.['100'] || "#ffffff" },
                        200: { value: customColors?.['200'] || "#ffffff" },
                        300: { value: customColors?.['300'] || "#ffffff" },
                        400: { value: customColors?.['400'] || "#ffffff" },
                        500: { value: customColors?.['500'] || "#ffffff" },
                        600: { value: customColors?.['600'] || "#ffffff" },
                        700: { value: customColors?.['700'] || "#ffffff" },
                        800: { value: customColors?.['800'] || "#ffffff" },
                        900: { value: customColors?.['900'] || "#ffffff" },
                        950: { value: customColors?.['950'] || "#ffffff" },
                    },
                },
                fonts: {
                    // Only NYSDS fonts
                    nysds: {
                        ...wrappedNysdsTokens.fonts
                    }
                },
                fontSizes: {
                    // Only NYSDS font sizes
                    nysds: {
                        ...wrappedNysdsTokens.fontSizes
                    }
                },
                fontWeights: {
                    // Only NYSDS font weights
                    nysds: {
                        ...wrappedNysdsTokens.fontWeights
                    }
                },
                lineHeights: {
                    // Only NYSDS line heights
                    nysds: {
                        ...wrappedNysdsTokens.lineHeights
                    }
                },
                letterSpacings: {
                    // Only NYSDS letter spacings
                    nysds: {
                        ...wrappedNysdsTokens.letterSpacings
                    }
                },
                spacing: {
                    // Only NYSDS space tokens
                    nysds: {
                        ...wrappedNysdsTokens.space
                    }
                },
                sizes: {
                    // Only NYSDS size tokens
                    nysds: {
                        ...wrappedNysdsTokens.sizes
                    }
                },
                borderWidths: {
                    // Only NYSDS border widths
                    nysds: {
                        ...wrappedNysdsTokens.borderWidths
                    }
                },
                radii: {
                    // Only NYSDS radii
                    nysds: {
                        ...wrappedNysdsTokens.radii
                    }
                },
                // gutters: { //TODO: Find correct TokenDefinition prop name
                //     // Only NYSDS gutters
                //     nysds: {
                //         ...nysdsPrimitiveTokens.gutters
                //     }
                // },
                animations: {
                    rotate90: { value: "rotate90 0.3s ease-in-out" }
                }
            },
            keyframes: {
                rotate90: {
                    "0%": { transform: "rotate(0deg)", animationFillMode: "forwards" },
                    "100%": { transform: "rotate(90deg)", animationFillMode: "forwards" },
                },
            },
            semanticTokens: {
                colors: {

                    gray: {
                        contrast: { value: { _light: '{colors.nysds.white}', _dark: '{colors.nysds.black}' } },
                        fg: { value: { _light: '{colors.nysds.neutral.800}', _dark: '{colors.nysds.neutral.200}' } },
                        subtle: { value: { _light: '{colors.nysds.neutral.100}', _dark: '{colors.nysds.neutral.900}' } },
                        muted: { value: { _light: '{colors.nysds.neutral.200}', _dark: '{colors.nysds.neutral.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.neutral.300}', _dark: '{colors.nysds.neutral.700}' } },
                        solid: { value: { _light: '{colors.nysds.neutral.900}', _dark: '{colors.nysds.white}' } },
                        focusRing: { value: { _light: '{colors.nysds.neutral.400}', _dark: '{colors.nysds.neutral.400}' } },
                    },

                    // NYSDS specific color semantic tokens
                    red: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.red.700}', _dark: '{colors.nysds.red.300}' } },
                        subtle: { value: { _light: '{colors.nysds.red.100}', _dark: '{colors.nysds.red.900}' } },
                        muted: { value: { _light: '{colors.nysds.red.200}', _dark: '{colors.nysds.red.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.red.300}', _dark: '{colors.nysds.red.700}' } },
                        solid: { value: { _light: '{colors.nysds.red.600}', _dark: '{colors.nysds.red.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.red.500}', _dark: '{colors.nysds.red.500}' } },
                    },
                    yellow: {
                        contrast: { value: { _light: 'black', _dark: 'black' } },
                        fg: { value: { _light: '{colors.nysds.yellow.700}', _dark: '{colors.nysds.yellow.300}' } },
                        subtle: { value: { _light: '{colors.nysds.yellow.100}', _dark: '{colors.nysds.yellow.900}' } },
                        muted: { value: { _light: '{colors.nysds.yellow.200}', _dark: '{colors.nysds.yellow.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.yellow.300}', _dark: '{colors.nysds.yellow.700}' } },
                        solid: { value: { _light: '{colors.nysds.yellow.500}', _dark: '{colors.nysds.yellow.500}' } },
                        focusRing: { value: { _light: '{colors.nysds.yellow.400}', _dark: '{colors.nysds.yellow.400}' } },
                    },
                    blue: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.blue.700}', _dark: '{colors.nysds.blue.300}' } },
                        subtle: { value: { _light: '{colors.nysds.blue.100}', _dark: '{colors.nysds.blue.900}' } },
                        muted: { value: { _light: '{colors.nysds.blue.200}', _dark: '{colors.nysds.blue.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.blue.300}', _dark: '{colors.nysds.blue.700}' } },
                        solid: { value: { _light: '{colors.nysds.blue.600}', _dark: '{colors.nysds.blue.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.blue.500}', _dark: '{colors.nysds.blue.500}' } },
                    },
                    green: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.green.700}', _dark: '{colors.nysds.green.300}' } },
                        subtle: { value: { _light: '{colors.nysds.green.100}', _dark: '{colors.nysds.green.900}' } },
                        muted: { value: { _light: '{colors.nysds.green.200}', _dark: '{colors.nysds.green.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.green.300}', _dark: '{colors.nysds.green.700}' } },
                        solid: { value: { _light: '{colors.nysds.green.600}', _dark: '{colors.nysds.green.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.green.500}', _dark: '{colors.nysds.green.500}' } },
                    },
                    stateBlue: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.stateBlue.700}', _dark: '{colors.nysds.stateBlue.300}' } },
                        subtle: { value: { _light: '{colors.nysds.stateBlue.100}', _dark: '{colors.nysds.stateBlue.900}' } },
                        muted: { value: { _light: '{colors.nysds.stateBlue.200}', _dark: '{colors.nysds.stateBlue.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.stateBlue.300}', _dark: '{colors.nysds.stateBlue.700}' } },
                        solid: { value: { _light: '{colors.nysds.stateBlue.600}', _dark: '{colors.nysds.stateBlue.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.stateBlue.500}', _dark: '{colors.nysds.stateBlue.500}' } },
                    },
                    businessTeal: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.businessTeal.700}', _dark: '{colors.nysds.businessTeal.300}' } },
                        subtle: { value: { _light: '{colors.nysds.businessTeal.100}', _dark: '{colors.nysds.businessTeal.900}' } },
                        muted: { value: { _light: '{colors.nysds.businessTeal.200}', _dark: '{colors.nysds.businessTeal.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.businessTeal.300}', _dark: '{colors.nysds.businessTeal.700}' } },
                        solid: { value: { _light: '{colors.nysds.businessTeal.600}', _dark: '{colors.nysds.businessTeal.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.businessTeal.500}', _dark: '{colors.nysds.businessTeal.500}' } },
                    },
                    healthPurple: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.healthPurple.700}', _dark: '{colors.nysds.healthPurple.300}' } },
                        subtle: { value: { _light: '{colors.nysds.healthPurple.100}', _dark: '{colors.nysds.healthPurple.900}' } },
                        muted: { value: { _light: '{colors.nysds.healthPurple.200}', _dark: '{colors.nysds.healthPurple.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.healthPurple.300}', _dark: '{colors.nysds.healthPurple.700}' } },
                        solid: { value: { _light: '{colors.nysds.healthPurple.600}', _dark: '{colors.nysds.healthPurple.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.healthPurple.500}', _dark: '{colors.nysds.healthPurple.500}' } },
                    },
                    localBrown: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.localBrown.700}', _dark: '{colors.nysds.localBrown.300}' } },
                        subtle: { value: { _light: '{colors.nysds.localBrown.100}', _dark: '{colors.nysds.localBrown.900}' } },
                        muted: { value: { _light: '{colors.nysds.localBrown.200}', _dark: '{colors.nysds.localBrown.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.localBrown.300}', _dark: '{colors.nysds.localBrown.700}' } },
                        solid: { value: { _light: '{colors.nysds.localBrown.600}', _dark: '{colors.nysds.localBrown.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.localBrown.500}', _dark: '{colors.nysds.localBrown.500}' } },
                    },
                    environmentGreen: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.environmentGreen.700}', _dark: '{colors.nysds.environmentGreen.300}' } },
                        subtle: { value: { _light: '{colors.nysds.environmentGreen.100}', _dark: '{colors.nysds.environmentGreen.900}' } },
                        muted: { value: { _light: '{colors.nysds.environmentGreen.200}', _dark: '{colors.nysds.environmentGreen.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.environmentGreen.300}', _dark: '{colors.nysds.environmentGreen.700}' } },
                        solid: { value: { _light: '{colors.nysds.environmentGreen.600}', _dark: '{colors.nysds.environmentGreen.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.environmentGreen.500}', _dark: '{colors.nysds.environmentGreen.500}' } },
                    },
                    adminOrange: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.adminOrange.700}', _dark: '{colors.nysds.adminOrange.300}' } },
                        subtle: { value: { _light: '{colors.nysds.adminOrange.100}', _dark: '{colors.nysds.adminOrange.900}' } },
                        muted: { value: { _light: '{colors.nysds.adminOrange.200}', _dark: '{colors.nysds.adminOrange.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.adminOrange.300}', _dark: '{colors.nysds.adminOrange.700}' } },
                        solid: { value: { _light: '{colors.nysds.adminOrange.600}', _dark: '{colors.nysds.adminOrange.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.adminOrange.500}', _dark: '{colors.nysds.adminOrange.500}' } },
                    },
                    transportationBlue: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.transportationBlue.700}', _dark: '{colors.nysds.transportationBlue.300}' } },
                        subtle: { value: { _light: '{colors.nysds.transportationBlue.100}', _dark: '{colors.nysds.transportationBlue.900}' } },
                        muted: { value: { _light: '{colors.nysds.transportationBlue.200}', _dark: '{colors.nysds.transportationBlue.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.transportationBlue.300}', _dark: '{colors.nysds.transportationBlue.700}' } },
                        solid: { value: { _light: '{colors.nysds.transportationBlue.600}', _dark: '{colors.nysds.transportationBlue.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.transportationBlue.500}', _dark: '{colors.nysds.transportationBlue.500}' } },
                    },
                    safetyGray: {
                        contrast: { value: { _light: 'white', _dark: 'white' } },
                        fg: { value: { _light: '{colors.nysds.safetyGray.700}', _dark: '{colors.nysds.safetyGray.300}' } },
                        subtle: { value: { _light: '{colors.nysds.safetyGray.100}', _dark: '{colors.nysds.safetyGray.900}' } },
                        muted: { value: { _light: '{colors.nysds.safetyGray.200}', _dark: '{colors.nysds.safetyGray.800}' } },
                        emphasized: { value: { _light: '{colors.nysds.safetyGray.300}', _dark: '{colors.nysds.safetyGray.700}' } },
                        solid: { value: { _light: '{colors.nysds.safetyGray.600}', _dark: '{colors.nysds.safetyGray.600}' } },
                        focusRing: { value: { _light: '{colors.nysds.safetyGray.500}', _dark: '{colors.nysds.safetyGray.500}' } },
                    },
                    // Semantic tokens for NYSDS derived colors with _dark variants
                    nysds: {
                        theme: { value: { _light: '{colors.nysds.stateBlue.700}', _dark: '{colors.nysds.stateBlue.300}' } },
                        themeFaint: { value: { _light: '{colors.nysds.stateBlue.10}', _dark: '{colors.nysds.stateBlue.900}' } },
                        themeWeaker: { value: { _light: '{colors.nysds.stateBlue.50}', _dark: '{colors.nysds.stateBlue.800}' } },
                        themeWeak: { value: { _light: '{colors.nysds.stateBlue.100}', _dark: '{colors.nysds.stateBlue.700}' } },
                        themeMid: { value: { _light: '{colors.nysds.stateBlue.500}', _dark: '{colors.nysds.stateBlue.500}' } },
                        themeStrong: { value: { _light: '{colors.nysds.stateBlue.800}', _dark: '{colors.nysds.stateBlue.200}' } },
                        themeStronger: { value: { _light: '{colors.nysds.stateBlue.900}', _dark: '{colors.nysds.stateBlue.100}' } },
                        accent: { value: { _light: '{colors.nysds.yellow.400}', _dark: '{colors.nysds.yellow.400}' } },
                        base: { value: { _light: '{colors.nysds.neutral.600}', _dark: '{colors.nysds.neutral.400}' } },
                        baseWeak: { value: { _light: '{colors.nysds.neutral.10}', _dark: '{colors.nysds.neutral.900}' } },
                        success: { value: { _light: '{colors.nysds.green.600}', _dark: '{colors.nysds.green.400}' } },
                        successWeak: { value: { _light: '{colors.nysds.green.50}', _dark: '{colors.nysds.green.800}' } },
                        info: { value: { _light: '{colors.nysds.blue.600}', _dark: '{colors.nysds.blue.400}' } },
                        infoWeak: { value: { _light: '{colors.nysds.blue.50}', _dark: '{colors.nysds.blue.800}' } },
                        warning: { value: { _light: '{colors.nysds.yellow.400}', _dark: '{colors.nysds.yellow.400}' } },
                        warningWeak: { value: { _light: '{colors.nysds.yellow.50}', _dark: '{colors.nysds.yellow.800}' } },
                        danger: { value: { _light: '{colors.nysds.red.600}', _dark: '{colors.nysds.red.400}' } },
                        dangerWeak: { value: { _light: '{colors.nysds.red.50}', _dark: '{colors.nysds.red.800}' } },
                        emergency: { value: { _light: '{colors.nysds.red.800}', _dark: '{colors.nysds.red.200}' } },
                        emergencyWeak: { value: { _light: '{colors.nysds.red.300}', _dark: '{colors.nysds.red.700}' } },
                        ink: { value: { _light: '{colors.nysds.neutral.900}', _dark: '{colors.nysds.white}' } },
                        inkReverse: { value: { _light: '{colors.nysds.white}', _dark: '{colors.nysds.neutral.900}' } },
                        text: { value: { _light: '{colors.nysds.neutral.900}', _dark: '{colors.nysds.white}' } },
                        textWeak: { value: { _light: '{colors.nysds.neutral.700}', _dark: '{colors.nysds.neutral.300}' } },
                        textWeaker: { value: { _light: '{colors.nysds.neutral.500}', _dark: '{colors.nysds.neutral.500}' } },
                        textWeakest: { value: { _light: '{colors.nysds.neutral.200}', _dark: '{colors.nysds.neutral.800}' } },
                        textDisabled: { value: { _light: '{colors.nysds.neutral.200}', _dark: '{colors.nysds.neutral.700}' } },
                        link: { value: { _light: '{colors.nysds.blue.600}', _dark: '{colors.nysds.blue.300}' } },
                        linkStrong: { value: { _light: '{colors.nysds.blue.700}', _dark: '{colors.nysds.blue.200}' } },
                        linkStrongest: { value: { _light: '{colors.nysds.blue.800}', _dark: '{colors.nysds.blue.100}' } },
                        linkNeutral: { value: { _light: '{colors.nysds.neutral.900}', _dark: '{colors.nysds.white}' } },
                        textReverse: { value: { _light: '{colors.nysds.white}', _dark: '{colors.nysds.neutral.900}' } },
                        textReverseWeak: { value: { _light: '{colors.nysds.neutral.100}', _dark: '{colors.nysds.neutral.800}' } },
                        textReverseWeaker: { value: { _light: '{colors.nysds.neutral.200}', _dark: '{colors.nysds.neutral.700}' } },
                        textReverseDisabled: { value: { _light: '{colors.nysds.neutral.600}', _dark: '{colors.nysds.neutral.300}' } },
                        linkReverse: { value: { _light: '{colors.nysds.neutral.300}', _dark: '{colors.nysds.neutral.600}' } },
                        linkReverseStrong: { value: { _light: '{colors.nysds.neutral.50}', _dark: '{colors.nysds.neutral.900}' } },
                        linkReverseStrongest: { value: { _light: '{colors.nysds.white}', _dark: '{colors.nysds.neutral.900}' } },
                        linkReverseNeutral: { value: { _light: '{colors.nysds.white}', _dark: '{colors.nysds.neutral.900}' } },
                        surface: { value: { _light: '{colors.nysds.white}', _dark: '{colors.nysds.neutral.900}' } },
                        surfaceReverse: { value: { _light: '{colors.nysds.neutral.900}', _dark: '{colors.nysds.white}' } },
                        surfaceRaised: { value: { _light: '{colors.nysds.neutral.10}', _dark: '{colors.nysds.neutral.800}' } },
                        focus: { value: { _light: '{colors.nysds.blue.600}', _dark: '{colors.nysds.blue.300}' } },
                    },
                    otda: {
                        solid: { value: { _light: "{colors.otda.500}", _dark: "{colors.otda.500}" } },
                        contrast: { value: { _light: "{colors.otda.100}", _dark: "{colors.otda.50}" } },
                        fg: { value: { _light: "{colors.otda.700}", _dark: "{colors.otda.100}" } },
                        muted: { value: { _light: "{colors.otda.100}", _dark: "{colors.otda.600}" } },
                        subtle: { value: { _light: "{colors.otda.200}", _dark: "{colors.otda.800}" } },
                        emphasized: { value: { _light: "{colors.otda.300}", _dark: "{colors.otda.500}" } },
                        focusRing: { value: { _light: "{colors.otda.500}", _dark: "{colors.otda.100}" } },
                    },
                },
                fonts: {
                    nysdsBody: { value: '{fonts.nysds.sans}' },
                    nysdsDisplay: { value: '{fonts.nysds.sans}' },
                    nysdsUi: { value: '{fonts.nysds.sans}' },
                    nysdsHeading: { value: '{fonts.nysds.sans}' },
                    nysdsAgency: { value: '{fonts.nysds.agency}' },
                },
                fontSizes: {
                    nysdsBodyMd: { value: '{fontSizes.nysds.md}' },
                    nysdsBodySm: { value: '{fontSizes.nysds.sm}' },
                    nysdsBodyXs: { value: '{fontSizes.nysds.xs}' },
                    nysdsH1: { value: '{fontSizes.nysds.7xl}' },
                    nysdsH2: { value: '{fontSizes.nysds.5xl}' },
                    nysdsH3: { value: '{fontSizes.nysds.4xl}' },
                    nysdsH4: { value: '{fontSizes.nysds.2xl}' },
                    nysdsH5: { value: '{fontSizes.nysds.2xl}' },
                    nysdsH6: { value: '{fontSizes.nysds.lg}' },
                    nysdsUiXs: { value: '{fontSizes.nysds.xs}' },
                    nysdsUiSm: { value: '{fontSizes.nysds.sm}' },
                    nysdsUiMd: { value: '{fontSizes.nysds.md}' },
                    nysdsUiLg: { value: '{fontSizes.nysds.lg}' },
                    nysdsUiXl: { value: '{fontSizes.nysds.xl}' },
                    nysdsDisplaySm: { value: '{fontSizes.nysds.6xl}' },
                    nysdsDisplayMd: { value: '{fontSizes.nysds.7xl}' },
                    nysdsDisplayLg: { value: '{fontSizes.nysds.8xl}' },
                    nysdsDisplayXl: { value: '{fontSizes.nysds.9xl}' },
                    nysdsAgencyXl: { value: '{fontSizes.nysds.2xl}' },
                    nysdsAgencyLg: { value: '{fontSizes.nysds.lg}' },
                    nysdsAgencyMd: { value: '{fontSizes.nysds.md}' },
                    nysdsIconXs: { value: '{sizes.nysds.iconXs}' },
                    nysdsIconSm: { value: '{sizes.nysds.iconSm}' },
                    nysdsIconMd: { value: '{sizes.nysds.iconMd}' },
                    nysdsIconLg: { value: '{sizes.nysds.iconLg}' },
                    nysdsIconXl: { value: '{sizes.nysds.iconXl}' },
                    nysdsIcon2xl: { value: '{sizes.nysds.icon2xl}' },
                    nysdsIcon3xl: { value: '{sizes.nysds.icon3xl}' },
                    nysdsIcon4xl: { value: '{sizes.nysds.icon4xl}' },
                    nysdsIcon5xl: { value: '{sizes.nysds.icon5xl}' },
                },
                lineHeights: {
                    nysdsBodyXs: { value: '{lineHeights.nysds.bodyXs}' },
                    nysdsBodySm: { value: '{lineHeights.nysds.bodySm}' },
                    nysdsBodyMd: { value: '{lineHeights.nysds.bodyMd}' },
                    nysdsUiXs: { value: '{lineHeights.nysds.uiXs}' },
                    nysdsUiSm: { value: '{lineHeights.nysds.uiSm}' },
                    nysdsUiMd: { value: '{lineHeights.nysds.uiMd}' },
                    nysdsUiLg: { value: '{lineHeights.nysds.uiLg}' },
                    nysdsUiXl: { value: '{lineHeights.nysds.uiXl}' },
                    nysdsDisplaySm: { value: '{lineHeights.nysds.displaySm}' },
                    nysdsDisplayMd: { value: '{lineHeights.nysds.displayMd}' },
                    nysdsDisplayLg: { value: '{lineHeights.nysds.displayLg}' },
                    nysdsDisplayXl: { value: '{lineHeights.nysds.displayXl}' },
                    nysdsH1: { value: '{lineHeights.nysds.h1}' },
                    nysdsH2: { value: '{lineHeights.nysds.h2}' },
                    nysdsH3: { value: '{lineHeights.nysds.h3}' },
                    nysdsH4: { value: '{lineHeights.nysds.h4}' },
                    nysdsH5: { value: '{lineHeights.nysds.h5}' },
                    nysdsH6: { value: '{lineHeights.nysds.h6}' },
                },
                letterSpacings: {
                    nysdsDisplaySm: { value: '{letterSpacings.nysds.0}' },
                    nysdsDisplayMd: { value: '{letterSpacings.nysds.0}' },
                    nysdsDisplayLg: { value: '{letterSpacings.nysds.negative100}' },
                    nysdsDisplayXl: { value: '{letterSpacings.nysds.negative100}' },
                    nysdsUiXs: { value: '{letterSpacings.nysds.600}' },
                    nysdsUiSm: { value: '{letterSpacings.nysds.400}' },
                    nysdsUiLg: { value: '{letterSpacings.nysds.300}' },
                    nysdsUiXl: { value: '{letterSpacings.nysds.200}' },
                    nysdsBodyMd: { value: '{letterSpacings.nysds.400}' },
                    nysdsH1: { value: '{letterSpacings.nysds.0}' },
                    nysdsH2: { value: '{letterSpacings.nysds.100}' },
                    nysdsH3: { value: '{letterSpacings.nysds.100}' },
                    nysdsH4: { value: '{letterSpacings.nysds.200}' },
                    nysdsH5: { value: '{letterSpacings.nysds.200}' },
                    nysdsH6: { value: '{letterSpacings.nysds.300}' },
                    nysdsUiMd: { value: '{letterSpacings.nysds.400}' },
                    nysdsBodySm: { value: '{letterSpacings.nysds.400}' },
                    nysdsBodyXs: { value: '{letterSpacings.nysds.600}' },
                },
                spacing: {
                    nysds: {
                        '1px': { value: '{space.nysds.1px}' },
                        '2px': { value: '{space.nysds.2px}' },
                        '50': { value: '{space.nysds.50}' },
                        '100': { value: '{space.nysds.100}' },
                        '150': { value: '{space.nysds.150}' },
                        '200': { value: '{space.nysds.200}' },
                        '250': { value: '{space.nysds.250}' },
                        '300': { value: '{space.nysds.300}' },
                        '400': { value: '{space.nysds.400}' },
                        '500': { value: '{space.nysds.500}' },
                        '600': { value: '{space.nysds.600}' },
                        '700': { value: '{space.nysds.700}' },
                        '800': { value: '{space.nysds.800}' },
                        '1200': { value: '{space.nysds.1200}' },
                        gutterXs: { value: '{gutters.nysds.xs}' },
                        gutterSm: { value: '{gutters.nysds.sm}' },
                        gutterMd: { value: '{gutters.nysds.md}' },
                        gutterLg: { value: '{gutters.nysds.lg}' },
                        gutterXl: { value: '{gutters.nysds.xl}' },
                    }
                },
                sizes: {
                    nysds: {
                        '1px': { value: '{sizes.nysds.1px}' },
                        '2px': { value: '{sizes.nysds.2px}' },
                        '50': { value: '{sizes.nysds.50}' },
                        '100': { value: '{sizes.nysds.100}' },
                        '150': { value: '{sizes.nysds.150}' },
                        '200': { value: '{sizes.nysds.200}' },
                        '250': { value: '{sizes.nysds.250}' },
                        '300': { value: '{sizes.nysds.300}' },
                        '400': { value: '{sizes.nysds.400}' },
                        '450': { value: '{sizes.nysds.450}' },
                        '500': { value: '{sizes.nysds.500}' },
                        '550': { value: '{sizes.nysds.550}' },
                        '600': { value: '{sizes.nysds.600}' },
                        '700': { value: '{sizes.nysds.700}' },
                        '800': { value: '{sizes.nysds.800}' },
                        '1200': { value: '{sizes.nysds.1200}' },
                        formSm: { value: '{sizes.nysds.formSm}' },
                        formMd: { value: '{sizes.nysds.formMd}' },
                        formLg: { value: '{sizes.nysds.formLg}' },
                        iconXs: { value: '{sizes.nysds.iconXs}' },
                        iconSm: { value: '{sizes.nysds.iconSm}' },
                        iconMd: { value: '{sizes.nysds.iconMd}' },
                        iconLg: { value: '{sizes.nysds.iconLg}' },
                        iconXl: { value: '{sizes.nysds.iconXl}' },
                        icon2xl: { value: '{sizes.nysds.icon2xl}' },
                        icon3xl: { value: '{sizes.nysds.icon3xl}' },
                        icon4xl: { value: '{sizes.nysds.icon4xl}' },
                        icon5xl: { value: '{sizes.nysds.icon5xl}' },
                    }
                },
                borderWidths: {
                    nysdsSm: { value: '{borderWidths.nysds.sm}' },
                    nysdsMd: { value: '{borderWidths.nysds.md}' },
                    nysdsLg: { value: '{borderWidths.nysds.lg}' },
                },
                radii: {
                    nysdsXs: { value: '{radii.nysds.xs}' },
                    nysdsSm: { value: '{radii.nysds.sm}' },
                    nysdsMd: { value: '{radii.nysds.md}' },
                    nysdsLg: { value: '{radii.nysds.lg}' },
                    nysdsXl: { value: '{radii.nysds.xl}' },
                    nysdsXxl: { value: '{radii.nysds.xxl}' },
                    nysdsRound: { value: '{radii.nysds.round}' },
                },
                // gutters: { //TODO: Find correct SemanticTokenDefinition prop name for this
                //     nysdsXs: { value: '{gutters.nysds.xs}' },
                //     nysdsSm: { value: '{gutters.nysds.sm}' },
                //     nysdsMd: { value: '{gutters.nysds.md}' },
                //     nysdsLg: { value: '{gutters.nysds.lg}' },
                //     nysdsXl: { value: '{gutters.nysds.xl}' },
                // },
            },
        },
    });
    return config;
};

export const system = (colorPalette: string, customColors?: Record<string, string>) => createSystem(defaultConfig, createTheme(colorPalette, customColors));